import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Building } from 'src/app/app/building/building.model';
import { BuildingService } from 'src/app/app/building/building.service';
import { Floor } from 'src/app/app/building/floor/floor.model';
import { FloorService } from 'src/app/app/building/floor/floor.service';
import { DictionaryService } from 'src/app/dictionary';
import { HeaderModel } from 'src/app/_component/header/header.model';
import { customValidators } from 'src/app/_helpers/customValidators';
import { globalHelper } from 'src/app/_helpers/global';
import { timePickerCustomTheme } from 'src/app/_helpers/timePickerTheme';
import { AttachmentImage } from 'src/app/_models/image.model';
import { SpaceAssignmentModel } from 'src/app/_models/space-assignment.model';
import { GetAssignmentsModel } from 'src/app/_models/space-requests.model';
import { UserService } from 'src/app/_services';
import { ErrorService } from 'src/app/_services/error.service';
import { HeaderService } from 'src/app/_services/header.service';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { RowsLoaderService } from 'src/app/_services/rows-loader.service';
import { SpaceService } from 'src/app/_services/space.service';
import { SpaceType } from 'src/app/_types/space-type';
import { SpaceModel } from '../../../../_models/space.model';
import { PaginationClientSideOptions } from '../../pagination-client-side/pagination-client-side-options';
import { PaginationColumn } from '../../pagination-client-side/pagination-column.class';
import { SelectDropdownComponent } from '../../select-dropdown/select-dropdown.component';
import { SpaceAssignmentEditComponent } from '../space-assignment-edit/space-assignment-edit.component';
import { SpaceAssignmentViewComponent } from '../space-assignment-view/space-assignment-view.component';
@Component({
  selector: 'app-space-edit',
  templateUrl: './space-edit.component.html',
  styleUrls: ['./space-edit.component.scss'],
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
})
export class SpaceEditComponent implements OnInit {
  form: FormGroup;
  initialBuilding: Building;
  floorList: Floor[];
  floorListDisabled: boolean = true;
  validationError: boolean = false;
  attachment: AttachmentImage = new AttachmentImage();
  spaceId: number = null;
  componentRef: any = SpaceAssignmentViewComponent;
  rowsLoader: RowsLoaderService;
  assignmentList: SpaceAssignmentModel[];
  placeDetailsLoaded: boolean;
  placeAssignmentsLoaded: boolean;
  disableSaveButton: boolean = false;
  hideBuildingDropdown: boolean;
  floorDoesntExist;
  isError: boolean = false;
  errorStatus: number;

  rerouteUrl: string;
  rerouteQuery: string;
  showTimePicker: boolean;
  showSpaceSubtypePicker: boolean;
  showSpaceCapacity: boolean;
  permissionAssignmentEdit: string;
  permissionSpaceEdit: string;
  permissionAssignmentAdd: string;

  isBuildingInput: boolean;
  isDisabled: boolean = false;

  timeError: boolean = false;
  timeFrom: Date = new Date(new Date().setHours(8, 0, 0, 0));
  timeTo: Date = new Date(new Date().setHours(19, 0, 0, 0));
  timeMin: string = '08:00';
  timeMax: string = '19:00';

  timePickerCustomTheme = timePickerCustomTheme;

  @Input()
  spaceType: SpaceType;

  spaceTypeEnum = SpaceType;

  @ViewChild('floorDropdown') dropdown: SelectDropdownComponent;

  constructor(
    public spaceServ: SpaceService,
    public floorServ: FloorService,
    public buildingServ: BuildingService,
    private router: Router,
    private acriveRoute: ActivatedRoute,
    public toastController: ToastController,
    public userServ: UserService,
    private alertController: AlertController,
    private translateService: TranslateService,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController,
    public dictionaryServ: DictionaryService,
    public p: ThisPlatform,
    private el: ElementRef,
    private errorService: ErrorService,
    private headerServ: HeaderService
  ) {}

  columnsDefinition: PaginationColumn[] = [
    {
      name: 'performedBy',
      displayName: 'SPACE.COMPANY',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.company
          ? row.company.name
          : this.translateService.instant('SPACE.ALL_USERS');
      },
    },
    {
      name: 'group',
      displayName: 'SPACE.GROUP',
      size: 3,
      sortable: true,
      colValue: (row) => {
        return row.group
          ? row.group.name
          : this.translateService.instant('SPACE.ALL_COMPANIES');
      },
    },
    {
      name: 'assignmentBy',
      displayName: 'SPACE.USER',
      size: 3,
      sortable: true,
      colValue: (row) => {
        return row.user
          ? row.user?.firstName + row.user?.lastName
          : this.translateService.instant('SPACE.ALL_USERS');
      },
    },
    {
      name: 'dateFrom',
      displayName: 'SPACE.ASSIGNMENT_START',
      size: 3,
      sortable: true,
    },
    {
      name: 'dateTo',
      displayName: 'SPACE.ASSIGNMENT_END',
      size: 3,
      sortable: true,
    },
  ];

  headerInit: HeaderModel = {
    title: '',
    showRightButtonsOnlyOnMobile: true,
  };

  options: PaginationClientSideOptions = {
    ...new PaginationClientSideOptions(),
    mainStyle: 'gray-md-rows',
    gridColumnsCount: 15,
    permissionEdit: false,
    mobileViewBreakPoint: 1700,
    headerEmitClick: true,
  };

  async ngOnInit() {
    this.initForm();
    await this.buildingServ.loadBuildingsSync();

    if (this.buildingServ.buildings.length === 1) {
      this.hideBuildingDropdown = true;
    } else {
      this.hideBuildingDropdown = false;
    }

    switch (this.spaceType) {
      case SpaceType.Parking:
        this.permissionAssignmentEdit = 'parking.space.assignment.edit';
        this.permissionAssignmentAdd = 'parking.space.assignment.add';
        this.permissionSpaceEdit = 'parking.spaces.add-edit';
        this.rerouteUrl = '/app/parking';
        this.rerouteQuery = 'parking_space';
        this.showTimePicker = false;
        this.showSpaceSubtypePicker = true;
        this.showSpaceCapacity = false;
        this.options.spaceType = SpaceType.Parking;
        break;
      case SpaceType.Desk:
        this.permissionAssignmentEdit = 'desk.space.assignment.edit';
        this.permissionAssignmentAdd = 'desk.space.assignment.add';
        this.permissionSpaceEdit = 'desk.spaces.add-edit';
        this.rerouteUrl = '/app/desk';
        this.rerouteQuery = 'desk_space';
        this.showTimePicker = false;
        this.showSpaceSubtypePicker = false;
        this.showSpaceCapacity = false;
        this.options.spaceType = SpaceType.Desk;
        break;
      case SpaceType.Room:
        this.permissionAssignmentEdit = 'room.space.assignment.edit';
        this.permissionAssignmentAdd = 'room.space.assignment.add';
        this.permissionSpaceEdit = 'booking.room.delete';
        this.rerouteUrl = '/app/booking';
        this.rerouteQuery = 'rooms';
        this.showTimePicker = true;
        this.showSpaceSubtypePicker = false;
        this.showSpaceCapacity = true;
        this.options.spaceType = SpaceType.Room;
        this.form.get('timeFrom').addValidators(Validators.required);
        this.form.get('timeTo').addValidators(Validators.required);
        break;
    }

    this.isDisabled = await this.userServ.hasRolesAsync([
      this.permissionSpaceEdit,
    ]);

    this.options.permissionEdit = await this.userServ.hasRolesAsync([
      this.permissionAssignmentEdit,
    ]);

    this.options.permissionSpaceAdd = await this.userServ.hasRolesAsync([
      this.permissionAssignmentAdd,
    ]);

    this.acriveRoute.params.subscribe(async (resp) => {
      this.spaceServ.assignmentList = [];
      this.form.reset();

      this.spaceId = resp.Id;

      if (this.spaceId) {
        switch (this.spaceType) {
          case SpaceType.Parking:
            this.headerInit.title = this.translateService.instant(
              'SPACE.PARKING.EDIT_PARKING_PLACE'
            );
            break;
          case SpaceType.Desk:
            {
              this.headerInit.title = this.translateService.instant(
                'SPACE.DESK.EDIT_DESK'
              );
            }

            break;
          case SpaceType.Room:
            this.headerInit.title = this.translateService.instant(
              'SPACE.ROOM.DETAIL'
            );
            break;
        }

        this.headerInit.rightButtons = [
          {
            iconName: 'trash',
            rolePermission: [this.permissionSpaceEdit],
            onClick: () => {
              this.deleteParkingPlace();
            },
          },
        ];

        this.loadPlaceDetails();
        this.loadSpaceAssignments();
      } else {
        if (this.userServ.userStorage.idBuilding) {
          this.initialBuilding = this.buildingServ.findBuilding(
            this.userServ.userStorage.idBuilding
          );
          this.floorServ
            .getFloors(this.userServ.userStorage.idBuilding)
            .subscribe((floorList: Floor[]) => {
              this.floorList = floorList;
              this.floorListDisabled = floorList.length > 0 ? false : true;
            });

          this.form.patchValue({
            building: this.initialBuilding,
          });
        }
        switch (this.spaceType) {
          case SpaceType.Parking:
            this.headerInit.title = this.translateService.instant(
              'SPACE.PARKING.ADD_NEW_PARKING_PLACE'
            );
            break;
          case SpaceType.Desk:
            this.headerInit.title = this.translateService.instant(
              'SPACE.DESK.ADD_DESK'
            );
            break;
          case SpaceType.Room:
            this.headerInit.title = this.translateService.instant(
              'SPACE.ROOM.ADD'
            );
            break;
        }

        this.placeDetailsLoaded = true;
        this.placeAssignmentsLoaded = true;
        if (this.spaceType === SpaceType.Room) {
          this.form.patchValue({
            timeFrom: '08:00',
            timeTo: '19:00',
          });
        }
        this.form.patchValue({
          isAutomaticShare: false,
        });
      }
    });
    this.headerServ.setHeaderInit(this.headerInit);
  }

  onTimeSelected(date, ifStartOfEnd: String = 'start' || 'end') {
    let parsedTime =
      date.getHours() + ':' + date.getMinutes().toString().padStart(2, '0');

    if (ifStartOfEnd == 'start') {
      this.form.patchValue({
        timeFrom: parsedTime,
      });
      this.timeMin = parsedTime;
    } else {
      this.form.patchValue({
        timeTo: parsedTime,
      });
      this.timeMax = parsedTime;
    }
    this.timeError = this.form.hasError('startTimeBigger');
  }

  onSaveSpace() {
    if (!this.form.valid) {
      this.validationError = true;
      globalHelper.scrollToFirstInvalidControl(this.el);
      return;
    }
    this.saveSpace(this.form.value);
  }

  onBuildingChange(event) {
    this.dropdown.writeValue(null);

    this.form.patchValue({
      floor: null,
    });

    if (event.length !== 0) {
      this.floorListDisabled = false;

      this.floorServ.getFloors(event.id).subscribe((floorList: Floor[]) => {
        this.floorList = floorList;
        this.dropdown.ngOnInit();
        if (!floorList.length) {
          this.floorListDisabled = true;
        }
      });
    } else {
      this.floorList = null;
      this.floorListDisabled = true;
    }
  }

  onActionRequest(action) {
    if (action.type == 'remove') {
      this.deleteAssignment(action);
    } else if (action.type == 'edit') {
      this.editAssignment(action);
    }
  }

  initForm() {
    this.isBuildingInput = this.buildingServ.buildings.length > 1;
    this.form = new FormGroup(
      {
        number: new FormControl(null, {
          updateOn: 'blur',
          validators: [
            Validators.required,
            Validators.maxLength(30),
            customValidators.trimValidator(),
          ],
        }),
        shortName: new FormControl('', {
          updateOn: 'blur',
          validators: [Validators.maxLength(3)],
        }),
        subtype: new FormControl(
          this.dictionaryServ.parkingSubtype
            ? this.dictionaryServ.parkingSubtype[0]
            : null
        ),
        building: new FormControl(null, {
          updateOn: 'blur',
          validators: this.isBuildingInput ? [Validators.required] : [],
        }),
        floor: new FormControl(null, {
          updateOn: 'blur',
          validators: [Validators.required],
        }),
        timeFrom: new FormControl(null, {
          updateOn: 'blur',
          validators: [customValidators.trimValidator()],
        }),
        timeTo: new FormControl(null, {
          updateOn: 'blur',
          validators: [customValidators.trimValidator()],
        }),
        capacity: new FormControl('', {
          updateOn: 'blur',
          validators:
            this.spaceType == SpaceType.Room ? [Validators.required, Validators.min(1)] : [],
        }),
        description: new FormControl('', {
          updateOn: 'blur',
          validators: [Validators.maxLength(500)],
        }),
        isAutomaticShare: new FormControl(false, {
          updateOn: 'blur',
        }),
        itemImage: new FormControl(null, {
          updateOn: 'blur',
          validators: [
            customValidators.fileMaxSize(10000000),
            customValidators.fileFormat([
              'jpg',
              'png',
              'gif',
              'jfif',
              'pjpeg',
              'jpeg',
              'pjp',
            ]),
          ],
        }),
        isAvailableAtWeekends: new FormControl(false, {
          updateOn: 'blur',
        }),
      },
      customValidators.timeValidator('timeFrom', 'timeTo')
    );
  }

  loadPlaceDetails() {
    this.spaceServ.getSpace(this.spaceId).subscribe(
      (space) => {
        this.placeDetailsLoaded = true;
        this.loadSpace(space);
      },
      (error) => {
        this.errorStatus = error.status;
        this.isError = true;
      }
    );
  }

  loadSpaceAssignments() {
    let requestDetails: GetAssignmentsModel = {
      idSpace: this.spaceId,
      shared: 'false',
    };

    this.spaceServ
      .getAssignments(requestDetails)
      .subscribe((resp: SpaceAssignmentModel[]) => {
        this.placeAssignmentsLoaded = true;
        this.spaceServ.assignmentList = resp;
        this.rowsLoaderInit();
      });
  }

  rowsLoaderServiceReady(loader: RowsLoaderService) {
    this.rowsLoader = loader;
  }

  rowsLoaderInit() {
    this.rowsLoader.init(this.spaceServ.assignmentList);
  }

  saveSpace(form) {
    const formData = new FormData();
    formData.append('name', form.number);
    formData.append('shortName', form.shortName ? form.shortName : '');
    formData.append('type', JSON.stringify(this.spaceType));
    formData.append('idFloor', form.floor.id);
    formData.append('description', form.description ? form.description : '');
    formData.append(
      'idBuilding',
      this.isBuildingInput
        ? form.building.id
        : this.buildingServ.findBuilding(this.userServ.userStorage.idBuilding)
            .id + ''
    );
    formData.append('subType', form.subtype ? form.subtype.value : 0);
    formData.append(
      'isAutomaticShare',
      form.isAutomaticShare ? form.isAutomaticShare : ''
    );
    form.itemImage ? formData.append('file', form.itemImage) : null;
    form.itemImage ? null : formData.append('deleteAttachments', 'true');

    if (this.spaceType === SpaceType.Room) {
      formData.append('timeFrom', form.timeFrom);
      formData.append('timeTo', form.timeTo);
      formData.append('capacity', form.capacity);
      formData.append(
        'isAvailableAtWeekends',
        form?.isAvailableAtWeekends ? form?.isAvailableAtWeekends : ''
      );
    }

    if (this.spaceServ.equipmentList.length > 0) {
      for (let i = 0; i < this.spaceServ.equipmentList.length; i++) {
        formData.append(
          `equipments[${i}][id]`,
          this.spaceServ.equipmentList[i].id.toString()
        );
      }
    }

    this.disableSaveButton = true;
    this.floorDoesntExist = false;

    if (!this.spaceId) {
      this.spaceServ.createSpace(formData).subscribe(() => {
        this.disableSaveButton = false;
        this.router.navigate([this.rerouteUrl], {
          queryParams: { section: this.rerouteQuery },
        });
      });
    } else {
      this.spaceServ.space.id = this.spaceId;
      this.spaceServ.updateSpace(formData).subscribe(
        () => {
          this.disableSaveButton = false;
          this.router.navigate([this.rerouteUrl], {
            queryParams: { section: this.rerouteQuery },
          });
        },
        (err) => {
          if (err.error.message === 'Floor not found') {
            this.floorDoesntExist = true;
            this.disableSaveButton = false;
          }
        }
      );
    }
  }

  loadSpace(space: SpaceModel) {
    this.floorServ
      .getFloors(space.floor.building.id, this.spaceType)
      .subscribe((floorList: Floor[]) => {
        this.floorList = floorList;
        this.floorListDisabled = floorList.length > 0 ? false : true;
      });

    this.form.patchValue({
      number: space.name,
      shortName: space.shortName,
      subtype: this.dictionaryServ.parkingSubtype
        ? this.dictionaryServ.parkingSubtype[space.subType]
        : null,
      building: space.floor.building,
      timeFrom: space.timeFrom,
      timeTo: space.timeTo,
      capacity: space.capacity,
      description: space.description,
      itemImage: space.image,
      isAutomaticShare: space.isAutomaticShare,
      isAvailableAtWeekends: space?.isAvailableAtWeekends,
    });

    // Rozdzielenie piętra od pozostałych elementów w pathValue jest konieczne. Żeby dropdown działał prawidłowo, obiek zaznaczony musi być taki sam, jak na liście, dlatego konieczne jest skasowanie ".building". A budynek musi być skasowany później, dopiero po uzupełnieniu pola z nazwą budynku.
    delete space.floor.building;

    this.form.patchValue({
      floor: space.floor,
    });

    this.spaceServ.equipmentList = space.equipments;

    this.attachment = space.image;

    if (this.spaceType == SpaceType.Room) {
      this.timeFrom = new Date(
        new Date().setHours(
          parseInt(space.timeFrom.split(':')[0]),
          parseInt(space.timeFrom.split(':')[1]),
          0,
          0
        )
      );

      this.timeTo = new Date(
        new Date().setHours(
          parseInt(space.timeTo.split(':')[0]),
          parseInt(space.timeTo.split(':')[1]),
          0,
          0
        )
      );
      this.timeMin = space.timeFrom;
      this.timeMax = space.timeTo;
    }
  }

  async deleteParkingPlace() {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateService
    );
    if (confirm) {
      this.loadingCtrl
        .create({
          keyboardClose: true,
          message: this.translateService.instant('DELETING'),
        })
        .then((loadingEl) => {
          loadingEl.present();
          this.spaceServ.deleteSpace(this.spaceId).subscribe(() => {
            loadingEl.dismiss();
            this.router.navigate([this.rerouteUrl], {
              queryParams: { section: this.rerouteQuery },
            });
          });
        });
    }
  }

  async deleteAssignment(action) {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateService
    );
    if (confirm) {
      this.loadingCtrl
        .create({
          keyboardClose: true,
          message: this.translateService.instant('DELETING'),
        })
        .then((loadingEl) => {
          loadingEl.present();
          this.spaceServ.deleteAssignment(action.id).subscribe(
            () => {
              loadingEl.dismiss();
              this.loadSpaceAssignments();
            },
            () => {
              loadingEl.dismiss();
              this.errorService.showPopupAlert(
                'ERROR.ASSIGNMENTS_WITH_BOOKINGS'
              );
            }
          );
        });
    }
  }

  addAssignment() {
    this.editAssignmentModal(this.spaceId);
  }

  editAssignment(action) {
    this.editAssignmentModal(this.spaceId, action.id);
  }

  backToPakingList() {
    this.router.navigate([this.rerouteUrl], {
      queryParams: { section: this.rerouteQuery },
    });
  }

  async editAssignmentModal(spaceId: number, assignmentId?: number) {
    const modal = await this.modalCtrl.create({
      component: SpaceAssignmentEditComponent,
      animated: true,
      cssClass: 'add-edit-modal add-edit-modal--edit-parking-space',
      componentProps: {
        spaceId: spaceId,
        assignmentId: assignmentId,
        headerText: this.translateService.instant(
          'SPACE.CHOOSE_COMPANY_OR_USER'
        ),
        showUserDropdown: true,
        showRegistrationNumberInput:
          this.options.spaceType == SpaceType.Parking ? true : false,
      },
    });
    modal.onDidDismiss().then((data) => {
      if (data.role !== 'backdrop') {
        this.loadSpaceAssignments();
      }
    });
    modal.present();
  }
  onSelectTouched(formControlName: string): void {
    this.form.get(formControlName).markAsTouched();
  }
  onFloorChange(event) {
    this.form.patchValue({
      floor: event,
    });
  }
}
